// Wrapper for a stateful timer that can either show as a centered modal OR
// shrink to a draggable floating widget pinned to one of the four corners.
//
// Behavior:
//   - Modal mode = dimmed backdrop + Esc closes (click outside intentionally
//     does NOT close, so a stray tap doesn't lose the live timer).
//   - Mini mode  = no backdrop; underlying page is fully interactive.
//                  Drag handle on top; on release, snap to nearest corner.
//                  Position persists per-session via localStorage.
//   - children is a render prop receiving `{ minimized }` so the same
//     timer-engine component stays mounted across mode switches — no state loss.

import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { ArrowsIn, ArrowsOut, X } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";

const STORAGE_KEY = "hs-timer-mini-corner";    // remembered corner per browser
const CORNERS = ["br", "tr", "bl", "tl"];

function loadCorner() {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    return CORNERS.includes(v) ? v : "tr";
  } catch { return "tr"; }
}
function saveCorner(c) {
  try { localStorage.setItem(STORAGE_KEY, c); } catch { /* ignore */ }
}

/** Snap an (x, y) screen position to the nearest corner of the viewport. */
function nearestCorner(x, y) {
  const cx = window.innerWidth / 2;
  const cy = window.innerHeight / 2;
  const right = x > cx;
  const bottom = y > cy;
  return `${bottom ? "b" : "t"}${right ? "r" : "l"}`;
}

/** Translate a corner code to top/right/bottom/left CSS positioning. */
function cornerStyle(corner) {
  const pad = "12px";
  const s = {};
  if (corner.includes("t")) s.top = pad; else s.bottom = pad;
  if (corner.includes("r")) s.right = pad; else s.left = pad;
  return s;
}

export default function TimerOverlay({ open, onClose, title, children }) {
  const [minimized, setMinimized] = useState(false);
  const [corner, setCorner] = useState(loadCorner);
  const [dragPos, setDragPos] = useState(null); // {x,y} while dragging
  const dragInfo = useRef(null);

  // Reset to full-modal mode each time the overlay (re)opens.
  useEffect(() => { if (open) setMinimized(false); }, [open]);

  // Esc closes (only when modal-sized).
  useEffect(() => {
    if (!open || minimized) return undefined;
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, minimized, onClose]);

  // ── Drag handlers (pointer events work for touch + mouse) ────────────
  const onPointerDown = (e) => {
    // Don't start a drag from a button click.
    if (e.target.closest("button")) return;
    e.currentTarget.setPointerCapture(e.pointerId);
    const rect = e.currentTarget.getBoundingClientRect();
    dragInfo.current = {
      startX: e.clientX,
      startY: e.clientY,
      offX: e.clientX - rect.left,
      offY: e.clientY - rect.top,
      width: rect.width,
      height: rect.height,
      moved: false,
    };
  };
  const onPointerMove = (e) => {
    if (!dragInfo.current) return;
    const dx = e.clientX - dragInfo.current.startX;
    const dy = e.clientY - dragInfo.current.startY;
    if (!dragInfo.current.moved && Math.hypot(dx, dy) < 4) return;
    dragInfo.current.moved = true;
    const { width, height, offX, offY } = dragInfo.current;
    const x = Math.max(0, Math.min(window.innerWidth - width, e.clientX - offX));
    const y = Math.max(0, Math.min(window.innerHeight - height, e.clientY - offY));
    setDragPos({ x, y });
  };
  const onPointerUp = (e) => {
    if (!dragInfo.current) return;
    if (dragInfo.current.moved) {
      const c = nearestCorner(e.clientX, e.clientY);
      setCorner(c);
      saveCorner(c);
    }
    dragInfo.current = null;
    setDragPos(null);
  };

  if (!open) return null;

  // Children can be a render-prop or static JSX. Render-prop gets `{ minimized }`
  // so the timer engine can hide non-essential UI when shrunk while staying
  // mounted (preserving elapsed time, phase, counter, etc.).
  const renderChildren = typeof children === "function"
    ? children({ minimized })
    : children;

  // ── Modal mode ───────────────────────────────────────────────────────
  if (!minimized) {
    return createPortal(
      <div
        className="fixed inset-0 z-50 grid place-items-center bg-ink/60 backdrop-blur-sm p-4"
        data-testid="timer-overlay-modal"
      >
        <div className="w-full max-w-md bg-surface rounded-2xl shadow-lift border border-line overflow-hidden">
          <div className="flex items-center justify-between px-5 py-3 border-b border-line">
            <div className="font-heading text-lg">{title || "Live timer"}</div>
            <div className="flex items-center gap-1">
              <Button
                size="icon" variant="ghost"
                onClick={() => setMinimized(true)}
                className="rounded-lg h-8 w-8"
                aria-label="Minimize timer"
                title="Shrink to a corner — drag anywhere on screen"
                data-testid="timer-minimize"
              >
                <ArrowsIn size={16} weight="bold" />
              </Button>
              <Button
                size="icon" variant="ghost"
                onClick={onClose}
                className="rounded-lg h-8 w-8"
                aria-label="Close timer"
                data-testid="timer-close"
              >
                <X size={16} weight="bold" />
              </Button>
            </div>
          </div>
          <div className="p-5 max-h-[80vh] overflow-y-auto">
            {renderChildren}
          </div>
        </div>
      </div>,
      document.body,
    );
  }

  // ── Mini (draggable) mode ────────────────────────────────────────────
  const style = dragPos
    ? { left: dragPos.x, top: dragPos.y, right: "auto", bottom: "auto" }
    : cornerStyle(corner);
  return createPortal(
    <div
      className="fixed z-50 w-[220px] bg-ink text-bone rounded-2xl shadow-lift border border-ink/40 overflow-hidden select-none touch-none"
      style={{ ...style, position: "fixed" }}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
      data-testid="timer-overlay-mini"
    >
      <div className="flex items-center justify-between px-3 py-1.5 cursor-grab active:cursor-grabbing border-b border-bone/10">
        <div className="hs-label text-[9px] text-bone/60">{title || "Timer"} · drag</div>
        <div className="flex items-center gap-0.5">
          <Button
            size="icon" variant="ghost"
            onClick={(e) => { e.stopPropagation(); setMinimized(false); }}
            className="rounded-lg h-6 w-6 text-bone hover:bg-bone/10 hover:text-bone"
            aria-label="Restore timer"
            data-testid="timer-restore"
          >
            <ArrowsOut size={12} weight="bold" />
          </Button>
          <Button
            size="icon" variant="ghost"
            onClick={(e) => { e.stopPropagation(); onClose?.(); }}
            className="rounded-lg h-6 w-6 text-bone hover:bg-bone/10 hover:text-bone"
            aria-label="Close timer"
            data-testid="timer-mini-close"
          >
            <X size={12} weight="bold" />
          </Button>
        </div>
      </div>
      <div className="px-3 py-3">
        {renderChildren}
      </div>
    </div>,
    document.body,
  );
}
